import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { DomainFlattened, EntityType } from '../entity.types';
import { EntityInfos } from './infos/EntityInfos';
import { EntityRestrictedList } from './components/EntityRestrictedList';
import { EntityBlockLine } from './components/EntityBlockLine';
import { getEntityIdUrlSearch } from '../charts/chartsUtils';
import { buildUrlWithParameters } from '../../utils/url-utils/urlUtils';
import { getPageName } from '../../utils/pageName';
import {
  EntityBlock,
  EntityBlockTitle,
} from '../components/EntityBlockComponents';

type Props = {
  domains: Array<DomainFlattened>;
  decisionCount: number;
  entityType: EntityType;
  entityId: string;
  entityName: string;
  dontShowHref?: boolean;
};

export const EntityDomains: FC<Props> = ({
  domains,
  decisionCount,
  entityName,
  entityId,
  entityType,
  dontShowHref,
}) => {
  const restrictedDisplayLimit = 10;

  const source = 'expertise_domain';

  const pageName = getPageName();

  const buildDomainUrl = (domainKey: string) =>
    buildUrlWithParameters('/search', {
      q: encodeURIComponent(entityName),
      [getEntityIdUrlSearch(entityType)]: entityId,
      domaine: encodeURIComponent(`["${domainKey}"]`),
      chrono: true,
      sourcePage: pageName,
    });

  const buildSubDomainUrl = (domainKey: string, subDomainKey: string) =>
    buildUrlWithParameters('/search', {
      q: encodeURIComponent(entityName),
      [getEntityIdUrlSearch(entityType)]: entityId,
      domaine: encodeURIComponent(
        `["${domainKey}",["${domainKey}","${subDomainKey}"]]`,
      ),
      chrono: true,
      sourcePage: pageName,
    });

  const buildUrl = ({
    parentDomainKey,
    key,
  }: {
    parentDomainKey?: string;
    key: string;
  }) => {
    if (parentDomainKey) return buildSubDomainUrl(parentDomainKey, key);
    return buildDomainUrl(key);
  };

  return (
    <EntityBlock>
      <EntityBlockTitle>
        <FormattedMessage id="entity/expertises/Domains.tsx/1" />
      </EntityBlockTitle>
      <EntityInfos
        decisionCount={decisionCount}
        entityType={entityType}
        lawyerId={entityId}
        entityName={entityName}
      />
      <div className="entity__block__value-line entity__block__head-label">
        <span>
          <FormattedMessage id="entity/expertises/Domains.tsx/2" />
        </span>
        <span>
          <FormattedMessage id="entity/expertises/Domains.tsx/3" />
        </span>
      </div>
      <EntityRestrictedList
        list={domains}
        limit={restrictedDisplayLimit}
        source={source}
      >
        {newDomains =>
          newDomains.map(domain => (
            <EntityBlockLine
              data-test-id={domain.key}
              key={domain.key}
              label={domain.categoryName}
              value={domain.count}
              url={dontShowHref ? undefined : buildUrl(domain)}
              source={source}
              indent={!!domain.parentDomainKey}
            />
          ))
        }
      </EntityRestrictedList>
    </EntityBlock>
  );
};
